import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getUserProfileServer, updateUserProfileServer } from "@/server/users";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { EditableField } from "@/components/settings/editable-field";
import { ChangeEmailDialog } from "@/components/settings/change-email-dialog";
import { extractApiErrorMessage } from "@/lib/api/error";
import { TimezoneSelector } from "@/components/workspace/timezone-selector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/workspace/$workspaceSlug/settings/account/profile")({
  loader: async () => {
    const user = await getUserProfileServer();
    return { user };
  },
  component: ProfilePage,
});

/**
 * Get initials from a name for avatar fallback
 * @param name - The name to extract initials from
 * @returns Up to 2 uppercase initials
 */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
      <div className="w-[260px] h-11 flex items-center px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm truncate">
        {value}
      </div>
    </div>
  );
}

function ProfilePage() {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const { user } = Route.useLoaderData();
  const router = useRouter();

  // Calculate display name
  const displayName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.first_name || user.email || t("pages:profile.notSet");

  const handleUpdateFullName = async (fullName: string): Promise<void> => {
    try {
      await updateUserProfileServer({
        data: { full_name: fullName },
      });

      toast.success(t("pages:profile.updateSuccess"));

      // Reload the page data to reflect changes
      router.invalidate();
    } catch (error) {
      const message = extractApiErrorMessage(error, t("pages:profile.updateFailed"));
      toast.error(message);
      throw error; // Re-throw to let EditableField handle the error state
    }
  };

  const handleUpdateLanguage = async (value: string) => {
    try {
      await updateUserProfileServer({
        data: { language: value },
      });
      await i18n.changeLanguage(value);
      toast.success(t("pages:profile.updateSuccess"));
      router.invalidate();
    } catch (error) {
      const message = extractApiErrorMessage(error, t("pages:profile.updateFailed"));
      toast.error(message);
    }
  };

  const handleUpdateTimezone = async (value: string) => {
    try {
      await updateUserProfileServer({
        data: { timezone: value },
      });
      toast.success(t("pages:profile.updateSuccess"));
      router.invalidate();
    } catch (error) {
      const message = extractApiErrorMessage(error, t("pages:profile.updateFailed"));
      toast.error(message);
    }
  };

  const handleEmailUpdateSuccess = () => {
    // Reload the page data to reflect changes
    router.invalidate();
  };

  return (
    <div className="p-6">
      <div className="max-w-2xl">
        <div className="bg-white">
          {/* 头像部分 */}
          <div className="mb-8">
            <h3 className="text-base text-black mb-2">{t("pages:profile.title")}</h3>
            <Avatar className="w-20 h-20">
              <AvatarImage src={user.image_url || undefined} alt={displayName} />
              <AvatarFallback className="text-2xl font-semibold bg-gray-200 text-gray-700">
                {getInitials(displayName)}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* 用户信息 - 可编辑 */}
          <div className="space-y-6">
            {/* Full Name */}
            <EditableField
              label={t("pages:profile.fullName")}
              value={displayName}
              onSave={handleUpdateFullName}
              placeholder={t("pages:profile.fullNamePlaceholder")}
            />

            {/* Email */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                {t("pages:profile.email")}
              </div>
              <div className="flex items-start gap-3">
                <div className="w-[260px] h-11 flex items-center px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900">
                  {user.email || t("pages:profile.notSet")}
                </div>
                <ChangeEmailDialog
                  currentEmail={user.email || ""}
                  onSuccess={handleEmailUpdateSuccess}
                />
              </div>
            </div>

            {/* Language */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                {t("pages:profile.language")}
              </Label>
              <Select value={user.language || "en"} onValueChange={handleUpdateLanguage}>
                <SelectTrigger className="w-[260px] h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">{t("pages:profile.languages.en")}</SelectItem>
                  <SelectItem value="zh">{t("pages:profile.languages.zh")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Timezone */}
            <div>
              <TimezoneSelector
                label={t("pages:profile.timezone")}
                value={user.timezone || "UTC"}
                onChange={handleUpdateTimezone}
                className="w-[260px]"
                triggerClassName="w-[260px] h-11"
              />
            </div>

            {/* Profile Info (read-only from onboarding) */}
            {user.phone_number && (
              <div className="pt-4 border-t border-gray-200 space-y-4">
                <h4 className="text-sm font-medium text-gray-500">
                  {t("pages:profile.contactInfo")}
                </h4>
                <ReadOnlyField label={t("pages:profile.phoneNumber")} value={user.phone_number} />
                {user.company_website && (
                  <ReadOnlyField
                    label={t("pages:profile.companyWebsite")}
                    value={user.company_website}
                  />
                )}
                {user.linkedin_url && <ReadOnlyField label="LinkedIn" value={user.linkedin_url} />}
                {user.whatsapp && <ReadOnlyField label="WhatsApp" value={user.whatsapp} />}
                {user.location && (
                  <ReadOnlyField label={t("pages:profile.location")} value={user.location} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
